import {
    useState
} from "react";
import {
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    NCoreUIKitToast,
    PageContainer,
    TextInput,
    Button,
    Text
} from "ncore-ui-kit";
import {
    type RouteProp,
    useNavigation,
    useRoute
} from "@react-navigation/native";
import type RootStackParamList from "../../navigation/type";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";

const ResetPassword = () => {
    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    const route = useRoute<RouteProp<RootStackParamList, "ResetPassword">>();
    const passedEmail = route.params?.email || "";

    const [
        code,
        setCode
    ] = useState("");
    const [
        newPassword,
        setNewPassword
    ] = useState("");
    const [
        newPasswordConfirm,
        setNewPasswordConfirm
    ] = useState("");

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState(false);

    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const handleReset = async () => {
        if (!code || !newPassword || !newPasswordConfirm) {
            NCoreUIKitToast.open({
                title: localize("reset-password-alert-fields-required")
            });
            return;
        }

        if (newPassword !== newPasswordConfirm) {
            NCoreUIKitToast.open({
                title: localize("reset-password-alert-password-mismatch")
            });
            return;
        }

        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await fetch(`${process.env.API_URL}/auth/reset-password`, {
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    email: passedEmail,
                    newPassword,
                    code
                }),
                method: "POST"
            });

            const data = await response.json();

            if (data.status === "success") {
                NCoreUIKitToast.open({
                    title: localize("reset-password-toast-success")
                });
                navigation.navigate("Login");
            } else {
                NCoreUIKitToast.open({
                    title: data.message || localize("reset-password-toast-failed")
                });
            }
        } catch {
            NCoreUIKitToast.open({
                title: localize("reset-password-toast-network-error")
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return <PageContainer
        isScrollable
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: spaces.spacingXl
                }
            ]}
        >
            <Text
                variant="titleLargeSize"
            >
                {localize("reset-password-page-title")}
            </Text>
            <Text
                style={[
                    stylesheet.subtitle,
                    {
                        marginBottom: spaces.spacingXl,
                        marginTop: spaces.spacingMd
                    }
                ]}
                variant="bodyLargeSize"
            >
                {localize("reset-password-page-subtitle")}
            </Text>
            <View
                style={stylesheet.formContainer}
            >
                <TextInput
                    placeholder={localize("reset-password-code-placeholder")}
                    spreadBehaviour="stretch"
                    onChangeText={setCode}
                    value={code}
                />
                <View
                    style={{
                        height: spaces.spacingMd
                    }}
                />
                <TextInput
                    placeholder={localize("reset-password-new-password-placeholder")}
                    onChangeText={setNewPassword}
                    spreadBehaviour="stretch"
                    value={newPassword}
                    variant="hidden"
                    secureTextEntry
                />
                <View
                    style={{
                        height: spaces.spacingMd
                    }}
                />
                <TextInput
                    placeholder={localize("reset-password-new-password-confirm-placeholder")}
                    onChangeText={setNewPasswordConfirm}
                    value={newPasswordConfirm}
                    spreadBehaviour="stretch"
                    variant="hidden"
                    secureTextEntry
                />
                <View
                    style={{
                        height: spaces.spacingLg
                    }}
                />
                <Button
                    title={localize("reset-password-submit-button")}
                    spreadBehaviour="stretch"
                    isLoading={isSubmitting}
                    onPress={handleReset}
                />
                <View
                    style={[
                        stylesheet.backButtonContainer,
                        {
                            marginTop: spaces.spacingMd
                        }
                    ]}
                >
                    <Button
                        title={localize("reset-password-back-to-login")}
                        onPress={() => navigation.navigate("Login")}
                        spreadBehaviour="stretch"
                        variant="ghost"
                    />
                </View>
            </View>
        </View>
    </PageContainer>;
};

export default ResetPassword;
